import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';

interface CreditLimitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreditLimitModal: React.FC<CreditLimitModalProps> = ({ open, onOpenChange }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const hasTrackedRef = useRef(false);
  const [canBuyCredits, setCanBuyCredits] = useState(false);

  useEffect(() => {
    if (open && token && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      fetch(`${API_BASE}/api/observability-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventType: 'credit_limit_modal_shown',
          userMessage: t('credit_limit_modal_shown') || 'Credit limit modal shown',
        }),
      }).catch((error) => {
        console.error('Error tracking credit limit modal shown:', error);
      });
    }
    if (!open) {
      hasTrackedRef.current = false;
    }
  }, [open, token, t]);

  useEffect(() => {
    if (!open || !token) {
      setCanBuyCredits(false);
      return;
    }

    let cancelled = false;

    const fetchEligibility = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/credit-packs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok && !cancelled) {
          const data = await response.json();
          setCanBuyCredits(data.eligible === true);
        }
      } catch (error) {
        console.error('Error fetching credit purchase eligibility:', error);
        if (!cancelled) {
          setCanBuyCredits(
            user?.tier === 'pro' || user?.tier === 'editorial'
          );
        }
      }
    };

    fetchEligibility();

    return () => {
      cancelled = true;
    };
  }, [open, token, user?.tier]);

  const handleGetMoreCredits = (): void => {
    onOpenChange(false);
    if (canBuyCredits) {
      navigate('/desk/usage-and-billing#buy-credits');
      return;
    }
    navigate('/desk/usage-and-billing');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('credit_limit_reached') || 'Credit Limit Reached'}</DialogTitle>
          <DialogDescription>
            {canBuyCredits
              ? t('credit_limit_reached_buy_message')
              : t('credit_limit_reached_message') || "You've reached the credit usage limit."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <CancelButton onClick={() => onOpenChange(false)}>
            {t('cancel') || 'Cancel'}
          </CancelButton>
          <Button dialogPrimary onClick={handleGetMoreCredits} className="inline-flex items-center gap-1.5">
            {canBuyCredits ? t('buy_extra_credits') : t('get_more_credits') || 'Get more credits'}
            <DialogPrimaryShortcut className="ml-0.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
